import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlexBox,
  Typography,
  Backdrop,
  InputField,
  AbsoluteBox,
  SvgIconButton,
  Grid,
  MuiBox,
  MuiContainer,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { NumPadAction } from 'components/NumPad';
import { Keyboard } from 'components/Keyboard';
import { useKeyboard } from 'hooks/useKeyboard';
import { DataEntryOverlayProps } from '../types';
import { useStyles } from './styled';

/**
 * Overlay used for entering data
 * e.g. PIN
 */

export const DataEntryOverlay = (props: DataEntryOverlayProps) => {
  const {
    onCancel,
    onConfirm,
    inputType,
    title,
    description,
    inputHint,
    loading,
    nextText,
    prevText,
    maxLength,
    hideForwardPrompt
  } = props;

  const [inputValue, setInputValue] = useKeyboard('');

  const classes = useStyles();
  const { i18n } = useTranslation();

  const handleConfirm = () => {
    if (onConfirm) onConfirm(inputValue);
  };
  const buttonClick = (_: any, v: string) => {
    if (maxLength) {
      if (v === 'delete' || v === 'clear' || inputValue.length < maxLength) {
        setInputValue(v);
      }
    } else {
      setInputValue(v);
    }
  };

  return (
    <Backdrop open sx={{ zIndex: 2147483647 }}>
      <Box bg="white" padding="5 0" height="100vh" width="100%" sx={{ borderRadius: '32px 32px 0 0' }}>
        <MuiContainer className={classes.dataSearchInputBlock}>
          <Box padding="0 16">
            {/* Header */}
            <Grid className={classes.dataSearchIcontBlock}>
              <SvgIconButton data-testid="custom-ticket-stock-x" onClick={onCancel} appearance="transparent">
                <SvgIcon name="exit_circle" size={65} />
              </SvgIconButton>
            </Grid>
            {/* Content */}
            <Grid item md={12} xl={12}>
              <MuiBox className={classes.dataentryTitleBox}>
                <Typography fontSize={64} fontWeight={600} align="center">
                  {title}
                </Typography>
              </MuiBox>
              <Box margin="0 0 7" flexOverride="none">
                <Typography fontSize={30} fontWeight={400} align="center">
                  {description}
                </Typography>
              </Box>
            </Grid>
            {/* Input */}
            <Box>
              <InputField autoFocus size="large" align="center" fullWidth placeholder={inputHint} value={inputValue} />
            </Box>
          </Box>
        </MuiContainer>
      </Box>

      {/* NumPadAction */}
      {inputType === 'numpad' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
          <FlexBox fullHeight align="center" direction="column">
            <NumPadAction
              onClick={buttonClick}
              isConfirmDisabled={!inputValue}
              isLoading={loading}
              onConfirm={handleConfirm}
              layout="column"
              hideForwardPrompt={hideForwardPrompt}
            />
          </FlexBox>
        </AbsoluteBox>
      )}

      {/* Keyboard */}
      {inputType === 'keyboard' && (
        <MuiBox className={classes.dataentryKeyboardBox}>
          <AbsoluteBox
            raised
            fullWidth
            bg="x-light-gray"
            height={480}
            bottom="0"
            left="0"
            data-testid="data-entry-overlay-keyboard"
          >
            <FlexBox fullHeight align="center" direction="column">
              <Keyboard
                language={i18n.language}
                onClick={buttonClick}
                isNextDisabled={!inputValue}
                isLoading={loading}
                onNext={handleConfirm}
                nextText={nextText}
                prevText={prevText}
                onPrev={onCancel}
                hideForwardPrompt={hideForwardPrompt}
              />
            </FlexBox>
          </AbsoluteBox>
        </MuiBox>
      )}
    </Backdrop>
  );
};
